import React from 'react'
import { FlatList, Pressable, Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { BottomDrawer } from '@dehaat/dds'
import SearchInput from 'app/components/reusable/SearchInput'
import RoundImageWithName from 'app/components/reusable/RoundImageWithName'
import { Crop } from 'app/farmer360/models/crop'
import { Node, VendorRequest } from 'app/models/RequestFlow'

type SimpleOption = { id: string; name: string }

type Props = {
  styles: any
  showCropSheet: boolean
  cropSearch: string
  setCropSearch: (value: string) => void
  cropLoading: boolean
  filteredCrops: Crop[]
  cropColumns: number
  selectedCropId: number
  onCloseCropSheet: () => void
  onSelectCrop: (crop: Crop) => void

  showVillageSheet: boolean
  villageSearch: string
  setVillageSearch: (value: string) => void
  filteredVillages: SimpleOption[]
  onCloseVillageSheet: () => void
  onSelectVillage: (item: SimpleOption) => void

  showDistrictSheet: boolean
  districtSearch: string
  setDistrictSearch: (value: string) => void
  filteredDistricts: SimpleOption[]
  onCloseDistrictSheet: () => void
  onSelectDistrict: (item: SimpleOption) => void

  showBlockSheet: boolean
  blockSearch: string
  setBlockSearch: (value: string) => void
  filteredBlocks: SimpleOption[]
  onCloseBlockSheet: () => void
  onSelectBlock: (item: SimpleOption) => void

  showInventoryNodeSheet: boolean
  inventoryNodeSearch: string
  setInventoryNodeSearch: (value: string) => void
  inventoryNodeLoading: boolean
  filteredInventoryNodes: Node[]
  onCloseInventoryNodeSheet: () => void
  onSelectInventoryNode: (node: Node) => void

  showVendorSheet: boolean
  vendorSheetHeading: string
  vendorSearch: string
  setVendorSearch: (value: string) => void
  vendorLoading: boolean
  vendorLoadingNext: boolean
  vendors: VendorRequest[]
  onCloseVendorSheet: () => void
  onSelectVendor: (vendor: VendorRequest) => void
  onVendorEndReached: () => void
}

const DemoCreateRequestSheets = ({
  styles,
  showCropSheet,
  cropSearch,
  setCropSearch,
  cropLoading,
  filteredCrops,
  cropColumns,
  selectedCropId,
  onCloseCropSheet,
  onSelectCrop,
  showVillageSheet,
  villageSearch,
  setVillageSearch,
  filteredVillages,
  onCloseVillageSheet,
  onSelectVillage,
  showDistrictSheet,
  districtSearch,
  setDistrictSearch,
  filteredDistricts,
  onCloseDistrictSheet,
  onSelectDistrict,
  showBlockSheet,
  blockSearch,
  setBlockSearch,
  filteredBlocks,
  onCloseBlockSheet,
  onSelectBlock,
  showInventoryNodeSheet,
  inventoryNodeSearch,
  setInventoryNodeSearch,
  inventoryNodeLoading,
  filteredInventoryNodes,
  onCloseInventoryNodeSheet,
  onSelectInventoryNode,
  showVendorSheet,
  vendorSheetHeading,
  vendorSearch,
  setVendorSearch,
  vendorLoading,
  vendorLoadingNext,
  vendors,
  onCloseVendorSheet,
  onSelectVendor,
  onVendorEndReached,
}: Props) => {
  const { t } = useTranslation()

  return (
    <>
      {showVendorSheet && (
        <BottomDrawer
          visible={showVendorSheet}
          heading={vendorSheetHeading}
          closeOnBackdropPress
          onClose={onCloseVendorSheet}
        >
          <View style={styles.sheetBody}>
            <SearchInput
              placeholder={t('demo_vendor_search_placeholder')}
              value={vendorSearch}
              onChange={setVendorSearch}
            />
            {vendorLoading && vendors.length === 0 ? (
              <Text style={styles.sheetHint}>{t('demo_vendor_sheet_loading')}</Text>
            ) : vendors.length === 0 ? (
              <Text style={styles.sheetHint}>{t('demo_list_empty')}</Text>
            ) : (
              <FlatList
                data={vendors}
                keyExtractor={item => String(item.id)}
                onEndReached={onVendorEndReached}
                onEndReachedThreshold={0.3}
                renderItem={({ item }) => {
                  const location = [item.block?.name, item.district?.name, item.state?.name]
                    .filter(Boolean)
                    .join(', ')
                  return (
                    <Pressable style={styles.sheetItem} onPress={() => onSelectVendor(item)}>
                      <Text style={styles.sheetItemText}>{item.company_name}</Text>
                      {location ? (
                        <Text style={styles.sheetItemSubtext}>{location}</Text>
                      ) : null}
                    </Pressable>
                  )
                }}
              />
            )}
            {vendorLoadingNext ? (
              <Text style={styles.sheetHint}>{t('demo_vendor_sheet_loading_more')}</Text>
            ) : null}
          </View>
        </BottomDrawer>
      )}

      {showCropSheet && (
        <BottomDrawer
          visible={showCropSheet}
          heading="Select crop"
          closeOnBackdropPress
          onClose={onCloseCropSheet}
        >
          <View style={styles.cropSheetBody}>
            <SearchInput
              placeholder="Search crops"
              value={cropSearch}
              onChange={setCropSearch}
            />
            {cropLoading ? (
              <Text style={styles.sheetHint}>Loading crops...</Text>
            ) : filteredCrops.length === 0 ? (
              <Text style={styles.sheetHint}>No crops found</Text>
            ) : (
              <FlatList
                data={filteredCrops}
                keyExtractor={item => item.id}
                key={`crop_cols_${cropColumns}`}
                numColumns={cropColumns}
                columnWrapperStyle={styles.cropGridRow}
                contentContainerStyle={styles.cropGridContainer}
                style={styles.cropGridList}
                renderItem={({ item }) => (
                  <RoundImageWithName
                    name={item.name}
                    imgUrl={item.image_url}
                    textNumberOfLines={2}
                    style={styles.cropGridItem}
                    imageStyle={[
                      styles.cropGridImage,
                      selectedCropId === Number(item.id) && styles.cropGridImageSelected,
                    ]}
                    handleItemClick={() => onSelectCrop(item)}
                  />
                )}
              />
            )}
          </View>
        </BottomDrawer>
      )}

      {showVillageSheet && (
        <BottomDrawer
          visible={showVillageSheet}
          heading="Select village"
          closeOnBackdropPress
          onClose={onCloseVillageSheet}
        >
          <View style={styles.sheetBody}>
            <SearchInput
              placeholder="Search village"
              value={villageSearch}
              onChange={setVillageSearch}
            />
            {filteredVillages.length === 0 ? (
              <Text style={styles.sheetHint}>No villages found</Text>
            ) : (
              <FlatList
                data={filteredVillages}
                keyExtractor={item => item.id}
                renderItem={({ item }) => (
                  <Pressable style={styles.sheetItem} onPress={() => onSelectVillage(item)}>
                    <Text style={styles.sheetItemText}>{item.name}</Text>
                  </Pressable>
                )}
              />
            )}
          </View>
        </BottomDrawer>
      )}

      {showDistrictSheet && (
        <BottomDrawer
          visible={showDistrictSheet}
          heading="Select district"
          closeOnBackdropPress
          onClose={onCloseDistrictSheet}
        >
          <View style={styles.sheetBody}>
            <SearchInput
              placeholder="Search district"
              value={districtSearch}
              onChange={setDistrictSearch}
            />
            {filteredDistricts.length === 0 ? (
              <Text style={styles.sheetHint}>No districts found</Text>
            ) : (
              <FlatList
                data={filteredDistricts}
                keyExtractor={item => item.id}
                renderItem={({ item }) => (
                  <Pressable style={styles.sheetItem} onPress={() => onSelectDistrict(item)}>
                    <Text style={styles.sheetItemText}>{item.name}</Text>
                  </Pressable>
                )}
              />
            )}
          </View>
        </BottomDrawer>
      )}

      {showBlockSheet && (
        <BottomDrawer
          visible={showBlockSheet}
          heading="Select block"
          closeOnBackdropPress
          onClose={onCloseBlockSheet}
        >
          <View style={styles.sheetBody}>
            <SearchInput
              placeholder="Search block"
              value={blockSearch}
              onChange={setBlockSearch}
            />
            {filteredBlocks.length === 0 ? (
              <Text style={styles.sheetHint}>No blocks found</Text>
            ) : (
              <FlatList
                data={filteredBlocks}
                keyExtractor={item => item.id}
                renderItem={({ item }) => (
                  <Pressable style={styles.sheetItem} onPress={() => onSelectBlock(item)}>
                    <Text style={styles.sheetItemText}>{item.name}</Text>
                  </Pressable>
                )}
              />
            )}
          </View>
        </BottomDrawer>
      )}

      {showInventoryNodeSheet && (
        <BottomDrawer
          visible={showInventoryNodeSheet}
          heading="Select inventory node"
          closeOnBackdropPress
          onClose={onCloseInventoryNodeSheet}
        >
          <View style={styles.sheetBody}>
            <SearchInput
              placeholder="Search inventory node"
              value={inventoryNodeSearch}
              onChange={setInventoryNodeSearch}
            />
            {inventoryNodeLoading ? (
              <Text style={styles.sheetHint}>Loading inventory nodes...</Text>
            ) : filteredInventoryNodes.length === 0 ? (
              <Text style={styles.sheetHint}>No inventory nodes found</Text>
            ) : (
              <FlatList
                data={filteredInventoryNodes}
                keyExtractor={item => String(item.id)}
                renderItem={({ item }) => (
                  <Pressable
                    style={styles.sheetItem}
                    onPress={() => onSelectInventoryNode(item)}
                  >
                    <Text style={styles.sheetItemText}>{item.name}</Text>
                  </Pressable>
                )}
              />
            )}
          </View>
        </BottomDrawer>
      )}
    </>
  )
}

export default DemoCreateRequestSheets
